import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';
import {
  STORAGE_KEYS,
  SESSION_CONFIG,
  USER_ROLES,
  ROLE_PERMISSIONS,
  ADMIN_ROLE_CODES,
  isAdminOrAbove,
  hasMinRole,
} from '../constants';
import { authService, ErrorHandler } from '../services';

// Initial authentication state
const initialAuthState = {
  // Core auth state
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,

  // Session management
  sessionStartTime: null,
  lastActivity: null,
  sessionExpiry: null,
  sessionWarning: false,

  // Error handling
  error: null,

  // Login state
  isLoggingIn: false,
  isLoggingOut: false,
};

// Auth action types
export const AUTH_ACTION_TYPES = {
  // Authentication actions
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',

  // Logout actions
  LOGOUT_START: 'LOGOUT_START',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',

  // Token management
  TOKEN_REFRESH_SUCCESS: 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_FAILURE: 'TOKEN_REFRESH_FAILURE',

  // Session management
  SESSION_UPDATE_ACTIVITY: 'SESSION_UPDATE_ACTIVITY',
  SESSION_WARNING: 'SESSION_WARNING',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_RENEWED: 'SESSION_RENEWED',

  // User updates
  USER_UPDATE: 'USER_UPDATE',

  // State management
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_ERROR: 'SET_ERROR',

  // Initialization
  INITIALIZE_AUTH: 'INITIALIZE_AUTH',
};

// Authentication reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTION_TYPES.LOGIN_START:
      return {
        ...state,
        isLoggingIn: true,
        error: null,
      };

    case AUTH_ACTION_TYPES.LOGIN_SUCCESS: {
      const { user, token, refreshToken } = action.payload;
      const now = Date.now();

      // Use API-provided token expiry if available, otherwise fallback to calculated duration
      let sessionExpiry = now + SESSION_CONFIG.TIMEOUT_DURATION;
      if (user.tokenExpired) {
        try {
          sessionExpiry = new Date(user.tokenExpired).getTime();
        } catch (error) {
          console.warn('Invalid tokenExpired format, using default duration');
        }
      }

      return {
        ...state,
        user,
        token,
        refreshToken,
        isAuthenticated: true,
        isLoggingIn: false,
        sessionStartTime: now,
        lastActivity: now,
        sessionExpiry,
        sessionWarning: false,
        error: null,
      };
    }

    case AUTH_ACTION_TYPES.LOGIN_FAILURE:
      return {
        ...state,
        isLoggingIn: false,
        error: action.payload,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
      };

    case AUTH_ACTION_TYPES.LOGOUT_START:
      return {
        ...state,
        isLoggingOut: true,
      };

    case AUTH_ACTION_TYPES.LOGOUT_SUCCESS:
      return {
        ...initialAuthState,
        isLoading: false,
        isLoggingOut: false,
      };

    case AUTH_ACTION_TYPES.TOKEN_REFRESH_SUCCESS: {
      const { token, refreshToken, tokenExpired } = action.payload;
      const now = Date.now();

      // Use API-provided token expiry if available, otherwise fallback to calculated duration
      let sessionExpiry = now + SESSION_CONFIG.TIMEOUT_DURATION;
      if (tokenExpired) {
        try {
          sessionExpiry = new Date(tokenExpired).getTime();
        } catch (error) {
          console.warn(
            'Invalid tokenExpired format in refresh, using default duration'
          );
        }
      }

      return {
        ...state,
        token,
        refreshToken: refreshToken || state.refreshToken,
        sessionExpiry,
        lastActivity: now,
      };
    }

    case AUTH_ACTION_TYPES.TOKEN_REFRESH_FAILURE:
      return {
        ...initialAuthState,
        isLoading: false,
        error: 'Session expired. Please log in again.',
      };

    case AUTH_ACTION_TYPES.SESSION_UPDATE_ACTIVITY: {
      const now = Date.now();
      return {
        ...state,
        lastActivity: now,
        sessionExpiry: now + SESSION_CONFIG.TIMEOUT_DURATION,
        sessionWarning: false,
      };
    }

    case AUTH_ACTION_TYPES.SESSION_WARNING:
      return {
        ...state,
        sessionWarning: true,
      };

    case AUTH_ACTION_TYPES.SESSION_EXPIRED:
      return {
        ...initialAuthState,
        isLoading: false,
        error: 'Your session has expired. Please log in again.',
      };

    case AUTH_ACTION_TYPES.SESSION_RENEWED: {
      const now = Date.now();
      return {
        ...state,
        sessionExpiry: now + SESSION_CONFIG.TIMEOUT_DURATION,
        sessionWarning: false,
        lastActivity: now,
      };
    }

    case AUTH_ACTION_TYPES.USER_UPDATE:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case AUTH_ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case AUTH_ACTION_TYPES.INITIALIZE_AUTH: {
      const { user, token, refreshToken, isAuthenticated } = action.payload;
      const now = Date.now();

      return {
        ...state,
        user,
        token,
        refreshToken,
        isAuthenticated,
        isLoading: false,
        sessionStartTime: isAuthenticated ? now : null,
        lastActivity: isAuthenticated ? now : null,
        sessionExpiry: isAuthenticated
          ? now + SESSION_CONFIG.TIMEOUT_DURATION
          : null,
      };
    }

    default:
      return state;
  }
};

// Create Auth Context
const AuthContext = createContext(undefined);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // Check if user has required admin permissions
  const validateAdminPermissions = useCallback(user => {
    if (
      !user.roles ||
      !user.roles.admin ||
      !Array.isArray(user.roles.admin) ||
      user.roles.admin.length === 0
    ) {
      const errorMessage =
        "Access denied. You don't have administrator permissions required for this application.";

      return errorMessage;
    }
    return null; // Valid permissions
  }, []);

  // Extract primary role from API roles structure
  const extractPrimaryRole = useCallback(roles => {
    if (!roles || typeof roles !== 'object') return null;

    if (roles.admin && Array.isArray(roles.admin)) {
      if (
        roles.admin.some(
          r => r === ADMIN_ROLE_CODES.SUPER_ADMIN || r === 'SA' || r === 'Super Administrator'
        )
      ) {
        return USER_ROLES.SUPER_ADMIN;
      }
      if (
        roles.admin.some(
          r => r === ADMIN_ROLE_CODES.ADMIN || r === 'A' || r === 'Administrator'
        )
      ) {
        return USER_ROLES.ADMIN;
      }
      if (
        roles.admin.some(
          r => r === ADMIN_ROLE_CODES.OPERATION || r === 'P' || r === 'Operation'
        )
      ) {
        return USER_ROLES.OPERATION;
      }
      if (
        roles.admin.some(
          r => r === ADMIN_ROLE_CODES.MARKETING || r === 'M' || r === 'Marketing'
        )
      ) {
        return USER_ROLES.MARKETING;
      }
      if (
        roles.admin.some(
          r => r === ADMIN_ROLE_CODES.STAFF || r === 'S' || r === 'Staff'
        )
      ) {
        return USER_ROLES.STAFF;
      }
    }

    // Define role priority mapping based on API structure
    const rolePriorityMap = {
      admin: USER_ROLES.ADMIN,
      operation: USER_ROLES.OPERATION,
      marketing: USER_ROLES.MARKETING,
    };

    // Extract roles in priority order (admin > operation > marketing)
    const priorityOrder = ['admin', 'operation', 'marketing'];

    for (const roleKey of priorityOrder) {
      if (
        roles[roleKey] &&
        Array.isArray(roles[roleKey]) &&
        roles[roleKey].length > 0
      ) {
        return rolePriorityMap[roleKey];
      }
    }

    // Fallback: if no recognized role found, default to the first available role
    const firstRoleKey = Object.keys(roles)[0];
    if (firstRoleKey && rolePriorityMap[firstRoleKey]) {
      return rolePriorityMap[firstRoleKey];
    }

    return null;
  }, []);

  // Utility function to check if user has specific permission
  const hasPermission = useCallback(
    permission => {
      if (!state.user || !state.isAuthenticated) return false;

      const userRole = state.user.role;
      if (!userRole) return false;

      const permissions = ROLE_PERMISSIONS[userRole] || [];
      return permissions.includes(permission);
    },
    [state.user, state.isAuthenticated]
  );

  // Utility function to check if user has specific role
  const hasRole = useCallback(
    role => {
      if (!state.user || !state.isAuthenticated) return false;
      return state.user.role === role;
    },
    [state.user, state.isAuthenticated]
  );

  // Utility function to check if user has any of the specified roles
  const hasAnyRole = useCallback(
    roles => {
      if (!state.user || !state.isAuthenticated) return false;
      return roles.includes(state.user.role);
    },
    [state.user, state.isAuthenticated]
  );

  // Enhanced role checking functions for API roles structure
  const hasApiRole = useCallback(
    (roleKey, specificRole = null) => {
      if (!state.user || !state.isAuthenticated || !state.user.roles)
        return false;

      const userRoles = state.user.roles;
      if (!userRoles[roleKey] || !Array.isArray(userRoles[roleKey]))
        return false;

      // If specific role is provided, check for it; otherwise just check if role key exists
      if (specificRole) {
        return userRoles[roleKey].includes(specificRole);
      }

      return userRoles[roleKey].length > 0;
    },
    [state.user, state.isAuthenticated]
  );

  // Check if user has any admin role
  const hasAdminRole = useCallback(() => {
    return hasApiRole('admin');
  }, [hasApiRole]);

  // Check if user has any operation role
  const hasOperationRole = useCallback(() => {
    return hasApiRole('operation');
  }, [hasApiRole]);

  // Check if user has any marketing role
  const hasMarketingRole = useCallback(() => {
    return hasApiRole('marketing');
  }, [hasApiRole]);

  // Check if user has Admin or Super Admin role
  const isUserAdminOrAbove = useCallback(() => {
    if (!state.user || !state.isAuthenticated) return false;
    return isAdminOrAbove(state.user);
  }, [state.user, state.isAuthenticated]);

  // Check if user satisfies minimum role hierarchy
  const hasRoleOrAbove = useCallback(
    minRole => {
      if (!state.user || !state.isAuthenticated) return false;
      return hasMinRole(state.user, minRole);
    },
    [state.user, state.isAuthenticated]
  );

  // Get all roles for the user from API structure
  const getAllUserRoles = useCallback(() => {
    if (!state.user || !state.user.roles) return [];

    const allRoles = [];
    Object.keys(state.user.roles).forEach(roleKey => {
      const rolesInCategory = state.user.roles[roleKey];
      if (Array.isArray(rolesInCategory)) {
        rolesInCategory.forEach(role => {
          allRoles.push(`${roleKey}.${role}`);
        });
      }
    });

    return allRoles;
  }, [state.user]);

  // Save auth data to localStorage
  const saveAuthData = useCallback((user, token, refreshToken) => {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());

      if (refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
    } catch (error) {
      console.error('Failed to save auth data:', error);
    }
  }, []);

  // Clear auth data from localStorage
  const clearAuthData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.SESSION_START);
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      localStorage.removeItem(STORAGE_KEYS.SESSION_WARNING_SHOWN);
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }
  }, []);

  // Login function
  const login = useCallback(
    async credentials => {
      dispatch({ type: AUTH_ACTION_TYPES.LOGIN_START });

      try {
        // Handle special case where we're already verified (from OTP verification)
        if (credentials._skipApiCall && credentials._userData) {
          const { user, token, refreshToken } = credentials._userData;

          // Check if user has admin permissions
          const permissionError = validateAdminPermissions(user);
          if (permissionError) {
            dispatch({
              type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
              payload: permissionError,
            });
            return { success: false, error: permissionError };
          }

          // Transform user data to include role for backward compatibility
          const transformedUser = {
            ...user,
            role: extractPrimaryRole(user.roles),
          };

          // Save to localStorage (already done by authService, but ensuring consistency)
          saveAuthData(transformedUser, token, refreshToken);

          // Update state
          dispatch({
            type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
            payload: { user: transformedUser, token, refreshToken },
          });

          return { success: true, user: transformedUser };
        }

        // Use real API service for login
        const result = await authService.login(
          credentials.email || credentials.username,
          credentials.password,
          credentials.deviceId
        );

        // Handle OTP required case
        if (result.requiresOtp) {
          dispatch({
            type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
            payload: null, // Clear error since this is expected
          });
          return result; // Return the OTP result
        }

        if (!result.success) {
          throw new Error(result.error || 'Login failed');
        }

        const { user, token, refreshToken } = result;

        // Check if user has admin permissions
        const permissionError = validateAdminPermissions(user);
        if (permissionError) {
          dispatch({
            type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
            payload: permissionError,
          });
          return { success: false, error: permissionError };
        }

        // Transform user data to include role for backward compatibility
        const transformedUser = {
          ...user,
          role: extractPrimaryRole(user.roles),
        };

        // Save to localStorage (already done by authService, but ensuring consistency)
        saveAuthData(transformedUser, token, refreshToken);

        // Update state
        dispatch({
          type: AUTH_ACTION_TYPES.LOGIN_SUCCESS,
          payload: { user: transformedUser, token, refreshToken },
        });

        return { success: true, user: transformedUser };
      } catch (error) {
        console.error('Login error:', error);

        // Use error handler to get user-friendly message
        const processedError = ErrorHandler.handleError(error);
        const errorMessage =
          processedError.userMessage || 'Login failed. Please try again.';

        dispatch({
          type: AUTH_ACTION_TYPES.LOGIN_FAILURE,
          payload: errorMessage,
        });

        return { success: false, error: errorMessage };
      }
    },
    [extractPrimaryRole, saveAuthData]
  );

  // Logout function
  const logout = useCallback(async () => {
    dispatch({ type: AUTH_ACTION_TYPES.LOGOUT_START });

    try {
      // Use real API service for logout
      const result = await authService.logout();

      // Clear local storage (already done by authService, but ensuring consistency)
      clearAuthData();

      // Update state
      dispatch({ type: AUTH_ACTION_TYPES.LOGOUT_SUCCESS });

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      clearAuthData();
      dispatch({ type: AUTH_ACTION_TYPES.LOGOUT_SUCCESS });
      return { success: true };
    }
  }, [clearAuthData]);

  // Check if token should be refreshed based on API expiration times
  const shouldRefreshToken = useCallback(() => {
    return authService.shouldRefreshToken(5); // 5 minutes threshold
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    try {
      // Use real API service for token refresh
      const result = await authService.refreshToken();

      if (!result.success) {
        throw new Error(result.error || 'Token refresh failed');
      }

      const { token, tokenExpired, user } = result;

      // Transform user data to include role for backward compatibility
      const transformedUser = user
        ? {
            ...user,
            role: extractPrimaryRole(user.roles),
          }
        : null;

      dispatch({
        type: AUTH_ACTION_TYPES.TOKEN_REFRESH_SUCCESS,
        payload: {
          token,
          refreshToken: token, // Using same token as refresh token
          tokenExpired,
        },
      });

      // Update user data in state if provided
      if (transformedUser) {
        dispatch({
          type: AUTH_ACTION_TYPES.USER_UPDATE,
          payload: transformedUser,
        });
      }
      window.location.reload();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);

      // Use error handler to process error
      const processedError = ErrorHandler.handleError(error);

      dispatch({ type: AUTH_ACTION_TYPES.TOKEN_REFRESH_FAILURE });
      return false;
    }
  }, [extractPrimaryRole]);

  // Update user profile
  const updateUser = useCallback(
    updates => {
      dispatch({
        type: AUTH_ACTION_TYPES.USER_UPDATE,
        payload: updates,
      });

      // Update localStorage
      try {
        const updatedUser = { ...state.user, ...updates };
        localStorage.setItem(
          STORAGE_KEYS.USER_DATA,
          JSON.stringify(updatedUser)
        );
      } catch (error) {
        console.error('Failed to update user data:', error);
      }
    },
    [state.user]
  );

  // Update session activity
  const updateActivity = useCallback(() => {
    if (state.isAuthenticated) {
      dispatch({ type: AUTH_ACTION_TYPES.SESSION_UPDATE_ACTIVITY });
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    }
  }, [state.isAuthenticated]);

  // Renew session
  const renewSession = useCallback(() => {
    if (state.isAuthenticated) {
      dispatch({ type: AUTH_ACTION_TYPES.SESSION_RENEWED });
    }
  }, [state.isAuthenticated]);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTION_TYPES.CLEAR_ERROR });
  }, []);

  // Get remaining token time based on API expiration
  const getRemainingTokenTime = useCallback(() => {
    return authService.getRemainingTokenTime();
  }, []);

  // Format remaining token time for display
  const formatRemainingTokenTime = useCallback(() => {
    const remainingTime = getRemainingTokenTime();
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [getRemainingTokenTime]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        const lastActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);

        if (token && userData) {
          const user = JSON.parse(userData);
          if (user && user.roles) {
            user.role = extractPrimaryRole(user.roles) || user.role;
          }
          const lastActivityTime = lastActivity
            ? parseInt(lastActivity, 10)
            : Date.now();
          const now = Date.now();

          // Check if session is still valid
          const isSessionValid =
            now - lastActivityTime < SESSION_CONFIG.TIMEOUT_DURATION;

          if (isSessionValid) {
            dispatch({
              type: AUTH_ACTION_TYPES.INITIALIZE_AUTH,
              payload: {
                user,
                token,
                refreshToken,
                isAuthenticated: true,
              },
            });
          } else {
            // Session expired, clear data
            clearAuthData();
            dispatch({
              type: AUTH_ACTION_TYPES.INITIALIZE_AUTH,
              payload: {
                user: null,
                token: null,
                refreshToken: null,
                isAuthenticated: false,
              },
            });
          }
        } else {
          dispatch({
            type: AUTH_ACTION_TYPES.INITIALIZE_AUTH,
            payload: {
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
            },
          });
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        clearAuthData();
        dispatch({
          type: AUTH_ACTION_TYPES.INITIALIZE_AUTH,
          payload: {
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          },
        });
      }
    };

    initializeAuth();
  }, [clearAuthData, extractPrimaryRole]);

  // Handle token expiration events from API service
  useEffect(() => {
    const handleTokenExpired = async event => {
      console.warn('Token expired event received, attempting refresh...');

      if (state.isAuthenticated) {
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
          // Refresh failed, logout user
          await logout();
        }
      }
    };

    window.addEventListener('token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('token-expired', handleTokenExpired);
    };
  }, [state.isAuthenticated, refreshToken, logout]);

  // Session activity tracking
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleActivity = () => updateActivity();

    // Add activity listeners
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [state.isAuthenticated, updateActivity]);

  // Session timeout management
  useEffect(() => {
    if (!state.isAuthenticated || !state.sessionExpiry) return;

    const checkSession = () => {
      const now = Date.now();
      const timeUntilExpiry = state.sessionExpiry - now;

      if (timeUntilExpiry <= 0) {
        // Session expired
        dispatch({ type: AUTH_ACTION_TYPES.SESSION_EXPIRED });
        clearAuthData();
      } else if (
        timeUntilExpiry <= SESSION_CONFIG.WARNING_DURATION &&
        !state.sessionWarning
      ) {
        // Show warning
        dispatch({ type: AUTH_ACTION_TYPES.SESSION_WARNING });
      }
    };

    const interval = setInterval(checkSession, SESSION_CONFIG.REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [
    state.isAuthenticated,
    state.sessionExpiry,
    state.sessionWarning,
    clearAuthData,
  ]);

  // Context value
  const value = {
    // State
    ...state,

    // Actions
    login,
    logout,
    refreshToken,
    updateUser,
    updateActivity,
    renewSession,
    clearError,

    // Token utilities
    shouldRefreshToken,
    getRemainingTokenTime,
    formatRemainingTokenTime,

    // Utilities (backward compatible)
    hasPermission,
    hasRole,
    hasAnyRole,

    // Enhanced API role utilities
    hasApiRole,
    hasAdminRole,
    hasOperationRole,
    hasMarketingRole,
    getAllUserRoles,

    // Admin above utilities
    isAdminOrAbove: isUserAdminOrAbove,
    hasRoleOrAbove,

    // Computed values (backward compatible)
    isAdmin: state.user?.role === USER_ROLES.ADMIN,
    isSuperAdmin: state.user?.role === USER_ROLES.SUPER_ADMIN,
    isAdminOrAboveUser: isUserAdminOrAbove(),
    isOperationManager: state.user?.role === USER_ROLES.OPERATION,
    isMarketingManager: state.user?.role === USER_ROLES.MARKETING,

    // Enhanced computed values for API roles
    isApiAdmin: hasAdminRole(),
    isApiOperation: hasOperationRole(),
    isApiMarketing: hasMarketingRole(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
